"use client";

import {
  Input as ChakraInput,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { type ReactNode, useCallback, useState } from "react";
import type {
  ControllerRenderProps,
  FieldErrors,
  Path,
  UseFormRegister,
} from "react-hook-form";
import { HiOutlineCalendar } from "react-icons/hi2";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface LabeledInputProps<T extends Record<string, any>> {
  name: Path<T>;
  label?: string;
  errors?: FieldErrors<T>;
  register?: UseFormRegister<T>;
  field?: ControllerRenderProps<T, Path<T>>;
  textColor?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  helpText?: string;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  /** When true, error text is not shown (e.g. parent Field shows a group error). Border can still use invalid state. */
  hideError?: boolean;
  /** Override invalid state (e.g. highlight all inputs when any field in a group has an error) */
  invalidOverride?: boolean;
  /** Attached to the text input for Playwright */
  "data-testid"?: string;
  [key: string]: any;
  mb?: number;
}

export const LabeledInput = <T extends Record<string, any>>({
  name,
  label,
  errors,
  register,
  field,
  textColor: textColorProp,
  placeholder,
  type = "text",
  required = false,
  helpText,
  leftElement,
  rightElement,
  hideError = false,
  invalidOverride,
  mb,
  ...restProps
}: LabeledInputProps<T>) => {
  const theme = useThemeTokens();
  const [show, setShow] = useState(false);
  const textColor = textColorProp || theme.text.primary;

  const handleClick = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  const isPassword = type === "password";
  const isDate = type === "date";

  // Helper function to get nested error from path like "students.0.firstName"
  const getNestedError = (
    errors: FieldErrors<T> | undefined,
    path: string,
  ): any => {
    if (!errors) return undefined;
    const parts = path.split(".");
    let current: any = errors;
    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      // Try to access as array index first, then as property
      const numPart = parseInt(part, 10);
      if (!isNaN(numPart) && Array.isArray(current)) {
        current = current[numPart];
      } else {
        current = current[part];
      }
    }
    return current;
  };

  const error = getNestedError(errors, String(name));
  const isInvalid = invalidOverride !== undefined ? invalidOverride : !!error;

  // Use field from Controller if provided, otherwise use register
  const baseInputProps = {
    fontSize: "sm",
    fontWeight: "500",
    borderRadius: "8px",
    borderWidth: "1px",
    borderColor: isInvalid ? theme.accent.primary : theme.borderGlass.default,
    bg: "transparent",
    color: theme.text.primary,
    h: "44px",
    px: 4,
    _placeholder: {
      color: theme.text.tertiary,
      fontWeight: "400",
    },
    _focus: {
      borderColor: theme.accent.primary,
      boxShadow: theme.focusRing,
      bg: `transparent !important`,
      color: `${theme.text.primary} !important`,
    },
    _invalid: {
      borderColor: theme.accent.primary,
    },
    _autofill: {
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: theme.text.primary,
      boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
      transition: "background-color 600000s 0s, color 600000s 0s",
      caretColor: theme.text.primary,
    },
    sx: {
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
        {
          WebkitBackgroundClip: "text !important",
          backgroundClip: "text !important",
          WebkitTextFillColor: `${theme.text.primary} !important`,
          boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
          transition:
            "background-color 600000s 0s, color 600000s 0s !important",
          caretColor: `${theme.text.primary} !important`,
        },
      "&:-internal-autofill-selected": {
        WebkitBackgroundClip: "text !important",
        backgroundClip: "text !important",
        WebkitTextFillColor: `${theme.text.primary} !important`,
        boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
      },
      "&:focus": {
        bg: `transparent !important`,
        color: `${theme.text.primary} !important`,
        boxShadow: `${theme.focusRing} !important`,
      },
      "&:focus:-webkit-autofill": {
        boxShadow: `${theme.focusRing} !important`,
      },
      "@media (hover: hover)": {
        "&:hover:not(:focus):not(:disabled)": {
          borderColor: theme.accent.primaryBorder,
          bg: "transparent",
        },
      },
    },
    transition: "all 0.18s ease-out",
  };

  // Filter out non-DOM props from restProps
  const {
    helpText: _,
    leftElement: __,
    rightElement: ___,
    ...domSafeProps
  } = restProps;

  // Adjust padding when left/right elements are present (extra pl for left to avoid $/digit overlap)
  const finalBaseInputProps: any = { ...baseInputProps };
  if (leftElement && rightElement) {
    finalBaseInputProps.pl = "40px";
    finalBaseInputProps.pr = "32px";
    delete finalBaseInputProps.px;
  } else if (leftElement) {
    finalBaseInputProps.pl = "40px";
    finalBaseInputProps.pr = 4;
    delete finalBaseInputProps.px;
  } else if (rightElement) {
    finalBaseInputProps.pl = 4;
    finalBaseInputProps.pr = "32px";
    delete finalBaseInputProps.px;
  }

  // Use field from Controller if provided, otherwise use register
  const inputProps = field
    ? {
        ...field,
        ...finalBaseInputProps,
        type: isPassword ? (show ? "text" : "password") : type,
        placeholder,
        ...domSafeProps,
      }
    : {
        ...(register ? register(name) : {}),
        ...finalBaseInputProps,
        type: isPassword ? (show ? "text" : "password") : type,
        placeholder,
        ...domSafeProps,
      };

  return (
    <FormControl isInvalid={isInvalid} mb={mb}>
      {label && (
        <FormLabel
          ms="4px"
          mb="5px"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          letterSpacing="-0.01em"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}

      {isPassword ? (
        <InputGroup size="md">
          {leftElement && (
            <InputLeftElement
              h="100%"
              pl={4}
              minW="40px"
              justifyContent="flex-start"
              pointerEvents="none"
            >
              {leftElement}
            </InputLeftElement>
          )}
          <ChakraInput {...inputProps} />
          <InputRightElement h="100%" pr={3}>
            <Icon
              color={theme.text.tertiary}
              onClick={handleClick}
              cursor="pointer"
              fontSize="18px"
              transition="color 0.18s ease-out"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    color: theme.text.secondary,
                  },
                },
              }}
              as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
            />
          </InputRightElement>
        </InputGroup>
      ) : isDate ? (
        <InputGroup size="md">
          {leftElement && (
            <InputLeftElement
              h="100%"
              pl={4}
              minW="40px"
              justifyContent="flex-start"
              pointerEvents="none"
            >
              {leftElement}
            </InputLeftElement>
          )}
          <ChakraInput
            {...inputProps}
            pr="44px" // Add padding for icon
            onClick={() => {
              // Ensure date picker opens when clicking anywhere on the input
              const input = document.querySelector(
                `input[name="${String(name)}"]`,
              ) as HTMLInputElement;
              if (input) {
                input.showPicker?.();
              }
            }}
            sx={{
              ...(inputProps.sx || {}),
              // Hide browser's default calendar icon but keep functionality
              "&::-webkit-calendar-picker-indicator": {
                opacity: 0,
                position: "absolute",
                right: "44px", // Position it behind our custom icon
                width: "100%",
                height: "100%",
                cursor: "pointer",
              },
            }}
          />
          <InputRightElement h="100%" pr={3} pointerEvents="none">
            <HiOutlineCalendar color={theme.accent.primary} size={18} />
          </InputRightElement>
        </InputGroup>
      ) : leftElement || rightElement ? (
        <InputGroup size="md">
          {leftElement && (
            <InputLeftElement
              h="100%"
              pl={4}
              minW="40px"
              justifyContent="flex-start"
              pointerEvents="none"
            >
              {leftElement}
            </InputLeftElement>
          )}
          <ChakraInput {...inputProps} />
          {rightElement && (
            <InputRightElement h="100%" pr={3} pointerEvents="none">
              {rightElement}
            </InputRightElement>
          )}
        </InputGroup>
      ) : (
        <ChakraInput {...inputProps} />
      )}

      {error && !hideError && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {error.message as string}
        </Text>
      )}
      {!error && helpText && (
        <FormHelperText mt={1.5}>
          <Text color={theme.text.primary} fontSize="xs">
            {helpText}
          </Text>
        </FormHelperText>
      )}
    </FormControl>
  );
};
